import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { SearchHitCarousel } from '#/components/SearchHitCarousel/SearchHitCarousel';
import { UnpublishedListing } from '#/components/UnpublishedListing/UnpublishedListing';
import { useAppConfig } from '#/config/AppConfigContext';
import { useFetchFn } from '#/hooks/useFetchFn';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import type { ApiUnpublishedVolop } from '#/modules/listing/volops/api/types/ApiUnpublishedVolop';
import { searchAlgoliaFetchRecommendedListings } from '#/modules/search/algolia/fetch/searchAlgoliaFetchRecommendedListings';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetVolopsRoutePath } from '#/modules/search/volops/routing/searchGetVolopsRoutePath';

import { getUnpublishedVolopPageSeeMoreText } from './getUnpublishedVolopPageSeeMoreText';

type Props = {
  unpublishedVolop: ApiUnpublishedVolop;
};

export function UnpublishedVolopPage({ unpublishedVolop }: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const { hasAts, listingName, id, org } = unpublishedVolop;

  useEffect(() => {
    trackViewListing({
      status: 'unpublished',
      hasAts,
      listingName,
      listingId: id,
      listingType: 'VOLOP',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [hasAts, id, listingName, org]);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedVolop.id,
  });

  const [recommendedListingsResult] = useFetchFn({
    fn: searchAlgoliaFetchRecommendedListings,
    args: [unpublishedVolop, appConfig],
  });

  const recommendedListings = recommendedListingsResult?.hits ?? [];

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query, locale });

      void navigate(
        searchGetVolopsRoutePath({
          locale,
          q: parsedQuery,
          filtersByName: { locationType },
          embedSearchWidgetId: null,
        }),
        {
          state: {
            searchFrom: 'Unpublished Volop Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [locale, navigate],
  );

  const seeMoreText = getUnpublishedVolopPageSeeMoreText({
    i18n,
    orgUrl: unpublishedVolop.org
      ? `${unpublishedVolop.org.url[locale]}#opportunities`
      : undefined,
    orgPublishedVolopsCount: unpublishedVolop.orgPublishedVolopsCount ?? 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Volunteer Opportunities')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Volop Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        recommendedListings.length > 0 && (
          <SearchHitCarousel
            headline={i18n(
              'Explore Similar Volunteer Opportunities on Idealist',
            )}
            results={recommendedListings}
            seeAll={{
              text: i18n('See all volunteer opportunities'),
              action: { type: 'button', onClick: () => search('') },
            }}
            listingType="VOLOP"
            carouselLocation="Volop Listing"
            fromListingId={unpublishedVolop.id}
          />
        )
      }
    />
  );
}
